import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useShop } from "@/contexts/ShopContext";
import { toast } from "sonner";
import { categoryFallbackImage } from "@/constants/categoryImages";

export default function OrderTaking() {
  const { currentShopId } = useShop();

  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("All");
  const [orderType, setOrderType] = useState("LiveCounter");
  const [cart, setCart] = useState({});
  const [customerName, setCustomerName] = useState("");
  const [placing, setPlacing] = useState(false);

  // ==========================================================
  // IMAGE URL
  // ==========================================================

  const getImageUrl = (imageUrl, category) => {
    if (!imageUrl) {
      return categoryFallbackImage(category);
    }

    // Already a complete URL
    if (
      imageUrl.startsWith("http://") ||
      imageUrl.startsWith("https://")
    ) {
      return imageUrl;
    }

    // Backend URL from .env
    const backendUrl =
      process.env.REACT_APP_BACKEND_URL ||
      "http://localhost:8000";

    // image_url example:
    // /api/files/kitchen-os/menu-images/xxxxx.png

    return `${backendUrl}${imageUrl}`;
  };

  // ==========================================================
  // LOAD MENU ITEMS
  // ==========================================================

  const load = useCallback(async () => {
    if (!currentShopId) {
      setItems([]);
      return;
    }

    try {
      const { data } = await api.get("/menu-items", {
        params: {
          shop_id: currentShopId,
        },
      });

      setItems(
        (data || []).filter(
          (item) => !item.is_removed
        ).reverse()
      );
    } catch (error) {
      console.error(
        "Failed to load menu items:",
        error
      );

      toast.error(
        error.response?.data?.detail ||
          "Failed to load menu items"
      );
    }
  }, [currentShopId]);

  useEffect(() => {
    load();
  }, [load]);

  // ==========================================================
  // RESET CATEGORY AND CART WHEN ORDER TYPE CHANGES
  // ==========================================================

  useEffect(() => {
    setCategory("All");
    setCart({});
  }, [orderType]);

  // ==========================================================
  // FILTER ITEMS BASED ON ORDER TYPE
  // ==========================================================

  const orderTypeItems = items.filter((item) => {
    if (orderType === "Takeaway") {
      return item.category === "Take Away";
    }

    // Live Counter
    // Take Away items should not appear
    return item.category !== "Take Away";
  });

  // ==========================================================
  // CATEGORIES
  // ==========================================================

  const categories = [
    "All",
    ...new Set(
      orderTypeItems.map(
        (item) => item.category
      )
    ),
  ];

  // ==========================================================
  // VISIBLE ITEMS
  // ==========================================================

  const visible =
    category === "All"
      ? orderTypeItems
      : orderTypeItems.filter(
          (item) =>
            item.category === category
        );

  // ==========================================================
  // ADD TO CART
  // ==========================================================

  const addToCart = (item) => {
    if (
      item.stock !== null &&
      (cart[item.id]?.qty || 0) >= item.stock
    ) {
      toast.error(
        `${item.name} is out of stock`
      );
      return;
    }

    setCart((prev) => ({
      ...prev,

      [item.id]: {
        item,
        qty:
          (prev[item.id]?.qty || 0) + 1,
      },
    }));
  };

  // ==========================================================
  // REMOVE FROM CART
  // ==========================================================

  const removeFromCart = (id) => {
    setCart((prev) => {
      const copy = {
        ...prev,
      };

      delete copy[id];

      return copy;
    });
  };

  // ==========================================================
  // CART LINES
  // ==========================================================

  const lines = Object.values(cart);

  // ==========================================================
  // TOTAL
  // ==========================================================

  const total = lines.reduce(
    (sum, line) =>
      sum +
      line.item.price * line.qty,
    0
  );

  // ==========================================================
  // PLACE ORDER
  // ==========================================================

  const placeOrder = async () => {
    if (!customerName.trim()) {
      toast.error(
        "Customer name is required"
      );
      return;
    }

    if (lines.length === 0) {
      toast.error(
        "Please add at least one item"
      );
      return;
    }

    setPlacing(true);

    try {
      await api.post("/orders", {
        shop_id: currentShopId,

        customer_name:
          customerName.trim(),

        order_type: orderType,

        lines: lines.map((line) => ({
          item_id: line.item.id,
          qty: line.qty,
        })),
      });

      toast.success(
        orderType === "LiveCounter"
          ? "Live Counter order sent to kitchen"
          : "Takeaway order created"
      );

      // Clear current ticket
      setCart({});
      setCustomerName("");

      // IMPORTANT:
      // No navigation here.
      // User remains on Order Taking page.

    } catch (error) {
      console.error(
        "Failed to place order:",
        error
      );

      toast.error(
        error.response?.data?.detail ||
          "Could not place order"
      );
    } finally {
      setPlacing(false);
    }
  };

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <section
      className="view"
      data-testid="order-taking-page"
    >

      {/* PAGE HEADER */}

      <div className="page-head">

        <div>

          <div className="eyebrow">
            Fast lane
          </div>

          <h1>
            Order Taking
          </h1>

          <p>
            Build a clean ticket in seconds.
            It appears in the kitchen queue
            immediately.
          </p>

        </div>

      </div>

      <div className="take-grid">

        {/* ====================================================
            MENU
        ===================================================== */}

        <div className="card">

          <div className="section-head">

            <div>

              <h2>
                Choose items
              </h2>

              <p>
                {orderTypeItems.length} active menu items
              </p>

            </div>

          </div>

          {/* ORDER TYPE */}

          <div className="type-toggle">

            <button
              className={
                orderType === "LiveCounter"
                  ? "sel"
                  : ""
              }
              onClick={() =>
                setOrderType(
                  "LiveCounter"
                )
              }
              data-testid="order-type-livecounter"
            >
              Live counter
            </button>

            <button
              className={
                orderType === "Takeaway"
                  ? "sel"
                  : ""
              }
              onClick={() =>
                setOrderType(
                  "Takeaway"
                )
              }
              data-testid="order-type-takeaway"
            >
              Takeaway
            </button>

          </div>

          {/* CATEGORY */}

          <div
            className="phone-cats"
            style={{
              padding:
                "14px 16px 0",
            }}
          >

            {categories.map(
              (cat) => (

                <span
                  key={cat}
                  className={
                    cat === category
                      ? "sel"
                      : ""
                  }
                  onClick={() =>
                    setCategory(cat)
                  }
                  data-testid={`category-filter-${cat}`}
                >
                  {cat}
                </span>

              )
            )}

          </div>

          {/* MENU GRID */}

          <div className="menu-grid">

            {visible.map(
              (item) => (

                <button
                  key={item.id}
                  className={`menu-item ${
                    item.stock === 0
                      ? "out"
                      : ""
                  }`}
                  onClick={() =>
                    addToCart(item)
                  }
                  disabled={item.stock === 0}
                  data-testid={`menu-item-${item.id}`}
                >

                  {/* SOLD OUT */}

                  {item.stock === 0 && (
                    <div className="out-tag">
                      SOLD OUT
                    </div>
                  )}

                  {/* QUANTITY */}

                  {cart[item.id] && (
                    <div className="qty-pill">
                      {cart[item.id].qty}
                    </div>
                  )}

                  {/* IMAGE */}

                  <div className="thumb">

                    <img
                      src={getImageUrl(
                        item.image_url,
                        item.category
                      )}
                      alt={item.name}
                      onError={(event) => {
                        event.currentTarget.onerror =
                          null;

                        event.currentTarget.src =
                          categoryFallbackImage(
                            item.category
                          );
                      }}
                    />

                  </div>

                  {/* ITEM DETAILS */}

                  <div className="body">

                    <span className="category">
                      {item.category}
                    </span>

                    <strong>
                      {item.name}
                    </strong>

                    <small>
                      ₹{item.price}
                    </small>

                  </div>

                </button>

              )
            )}

            {visible.length === 0 && (
              <div className="empty">
                No menu items yet —
                add some in Inventory
              </div>
            )}

          </div>

        </div>

        {/* ====================================================
            CURRENT TICKET
        ===================================================== */}

        <div className="card">

          <div className="section-head">

            <div>

              <h2>
                Current ticket
              </h2>

              <p>
                {lines.length} line items
              </p>

            </div>

          </div>

          <div className="ticket">

            {/* CUSTOMER */}

            <label className="field-label">
              Customer name *
            </label>

            <input
              className="input"
              value={customerName}
              onChange={(e) =>
                setCustomerName(
                  e.target.value
                )
              }
              placeholder="Required — table 4 / walk-in guest"
              data-testid="customer-name-input"
            />

            {/* CART */}

            <div className="cart">

              {lines.length === 0 && (
                <div className="empty">
                  Tap items to build the ticket
                </div>
              )}

              {lines.map(
                (line) => (

                  <div
                    className="cart-line"
                    key={line.item.id}
                    data-testid={`cart-line-${line.item.id}`}
                  >

                    <strong>
                      {line.item.name} ×{" "}
                      {line.qty}
                    </strong>

                    <span className="mono">
                      ₹
                      {line.item.price *
                        line.qty}
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        removeFromCart(
                          line.item.id
                        )
                      }
                      data-testid={`remove-cart-line-${line.item.id}`}
                    >
                      ✕
                    </button>

                  </div>

                )
              )}

            </div>

            {/* TOTAL */}

            <div className="ticket-total">

              <span className="field-label">
                Total
              </span>

              <strong className="mono">
                ₹{total}
              </strong>

            </div>

            {/* PAYMENT NOTE */}

            <p
              className="note"
              style={{
                padding:
                  "10px 0",
                border: 0,
              }}
            >
              {orderType ===
              "LiveCounter"
                ? "Live Counter → payment marked Paid automatically."
                : "Takeaway → payment collected at counter."}
            </p>

            {/* PLACE ORDER */}

            <button
              className="btn btn-primary"
              style={{
                width: "100%",
              }}
              disabled={
                lines.length === 0 ||
                placing
              }
              onClick={
                placeOrder
              }
              data-testid="place-order-btn"
            >
              {placing
                ? "Placing..."
                : "✓ Place order"}
            </button>

          </div>

        </div>

      </div>

    </section>
  );
}